import React, { useState, useEffect } from 'react';
import { Trash, WarningCircle } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

/**
 * Modal that asks the President to confirm deletion of a team member
 * with a REQUIRED reason (min 5 chars). The reason is sent to the
 * backend and recorded in the audit log.
 *
 * Props:
 *   open: boolean
 *   target: { id, name, email, role } | null
 *   onCancel: () => void
 *   onConfirm: async (reason: string) => void  // throw on failure to keep modal open
 */
const DeleteUserModal = ({ open, target, onCancel, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) { setReason(''); setError(null); setBusy(false); }
  }, [open, target?.id]);

  const trimmed = reason.trim();
  const valid = trimmed.length >= 5;

  const handleSubmit = async () => {
    if (!valid) { setError('Please enter a reason (at least 5 characters).'); return; }
    setBusy(true); setError(null);
    try {
      await onConfirm(trimmed);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to delete user.');
      setBusy(false);
    }
  };

  if (!target) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="delete-user-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3" style={{ fontFamily: 'Outfit' }}>
            <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
              <Trash size={22} weight="fill" />
            </div>
            <span data-testid="delete-user-modal-title">Remove {target.name}?</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700">
            <p className="font-semibold mb-1">This permanently removes <strong>{target.name}</strong> ({target.role}).</p>
            <p className="text-xs">
              Any open leads or quotations assigned to them will be orphaned and must be reassigned.
              The reason below will be recorded in the audit log.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#3B5A54] mb-1">
              Reason for removal <span className="text-rose-600">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="e.g. Resigned on 28 Feb 2026; offboarding complete"
              className="w-full px-3 py-2 border border-[#D5E8E4] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/30 focus:border-rose-500"
              data-testid="delete-user-reason-input"
              autoFocus
            />
            <p className="text-xs text-[#6D8E87] mt-1">
              {trimmed.length}/500 — minimum 5 characters.
            </p>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-2 rounded-md bg-rose-50 border border-rose-200 text-xs text-rose-700" data-testid="delete-user-error">
              <WarningCircle size={16} weight="fill" className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="px-4 py-2 border border-[#D5E8E4] rounded-lg text-sm font-medium text-[#3B5A54] hover:bg-[#F4F9F7] transition disabled:opacity-50"
              data-testid="delete-user-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!valid || busy}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-rose-600 text-white hover:bg-rose-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="delete-user-confirm-btn"
            >
              {busy ? 'Removing…' : 'Remove user'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteUserModal;
